import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react";
import {
  TerminationRecord,
  TerminationReason,
  NoticeCalculation,
  FinalPayComponent,
  CreateTerminationRequest,
  UpdateTerminationRequest,
  TerminationResponse,
} from "@/types/termination";
import TerminationService from "@/services/terminationService";
import { format } from "date-fns";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";

// Extended NoticeCalculation for frontend with additional fields
interface ExtendedNoticeCalculation extends NoticeCalculation {
  finalWeeks?: number;
  overrideWeeks?: {
    value: number;
    timestamp: string;
    reason: string;
  };
  overridePayThroughDate?: {
    value: string;
    timestamp: string;
    reason: string;
  };
}

// Extended FinalPayComponent for frontend with additional fields
interface ExtendedFinalPayComponent extends FinalPayComponent {
  name?: string; // For referencing components by name
  overrideValue?: {
    value: number;
    timestamp: string;
    reason: string;
  };
}

// Extended TerminationRecord for frontend to use extended types
interface ExtendedTerminationRecord extends TerminationRecord {
  notice_calculation?: ExtendedNoticeCalculation;
  final_pay_components?: ExtendedFinalPayComponent[];
}

interface TerminationContextType {
  // State
  currentTermination: ExtendedTerminationRecord | null;
  isLoading: boolean;
  error: string | null;
  validationErrors: Record<string, string>;

  // Actions
  getTermination: (employeeId: string) => Promise<void>;
  createTermination: (data: CreateTerminationRequest) => Promise<void>;
  updateTermination: (
    employeeId: string,
    data: UpdateTerminationRequest,
  ) => Promise<void>;
  submitTermination: (employeeId: string) => Promise<void>;

  // Seat handling (frontend-only state)
  seatAction: "keep" | "reduce";
  seatUpdateMode: "end_of_cycle" | "immediate";
  setSeatAction: (action: "keep" | "reduce") => void;
  setSeatUpdateMode: (mode: "end_of_cycle" | "immediate") => void;

  // Form field updates
  setTerminationReason: (reason: TerminationReason) => void;
  setTerminationDate: (date: string) => void;
  overrideNoticePeriod: (weeks: number, reason: string) => void;
  overridePayThroughDate: (date: string, reason: string) => void;
  overrideFinalPayComponent: (
    name: string,
    value: number,
    reason: string,
  ) => void;

  // Helpers
  validateTerminationData: () => boolean;
  formatDateForDisplay: (isoDate: string) => string;
  calculatePayThroughDate: (
    terminationDate: string,
    noticeWeeks: number,
  ) => string;
  hasActiveTermination: (employeeId: string) => Promise<boolean>;
  resetTermination: () => void;
}

// Create the context with a default undefined value
const TerminationContext = createContext<TerminationContextType | undefined>(
  undefined,
);

// Provider props
interface TerminationProviderProps {
  children: ReactNode;
}

/**
 * Provider component for the termination context
 */
export const TerminationProvider: React.FC<TerminationProviderProps> = ({
  children,
}) => {
  // State
  const [currentTermination, setCurrentTermination] =
    useState<ExtendedTerminationRecord | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  // Seat handling state
  const [seatAction, setSeatAction] = useState<"keep" | "reduce">("keep");
  const [seatUpdateMode, setSeatUpdateMode] = useState<
    "end_of_cycle" | "immediate"
  >("end_of_cycle");

  /**
   * Reset termination state
   */
  const resetTermination = useCallback(() => {
    setCurrentTermination(null);
    setError(null);
    setValidationErrors({});
  }, []);

  /**
   * Format date for display
   */
  const formatDateForDisplay = useCallback((isoDate: string): string => {
    return format(new Date(isoDate), "dd/MM/yyyy");
  }, []);

  /**
   * Calculate pay-through date based on termination date and notice weeks
   */
  const calculatePayThroughDate = useCallback(
    (terminationDate: string, noticeWeeks: number): string => {
      const date = new Date(terminationDate);
      date.setDate(date.getDate() + noticeWeeks * 7);
      return format(date, "yyyy-MM-dd");
    },
    [],
  );

  /**
   * Validate termination data before submission
   */
  const validateTerminationData = useCallback((): boolean => {
    if (!currentTermination) {
      setError("No termination data to validate");
      return false;
    }

    const errors: Record<string, string> = {};

    if (!currentTermination.termination_reason) {
      errors.reason = "Termination reason is required";
    }

    if (!currentTermination.termination_date) {
      errors.terminationDate = "Termination date is required";
    }

    // Add more validation as needed

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [currentTermination]);

  /**
   * Get termination record
   */
  const getTermination = useCallback(
    async (employeeId: string): Promise<void> => {
      setIsLoading(true);
      setError(null);

      try {
        const termination = await TerminationService.getTermination(employeeId);
        setCurrentTermination(termination as ExtendedTerminationRecord);
      } catch (err) {
        setError("Failed to fetch termination record");
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  /**
   * Create a new termination record
   */
  const createTermination = useCallback(
    async (data: CreateTerminationRequest): Promise<void> => {
      setIsLoading(true);
      setError(null);

      try {
        const termination = await TerminationService.createTermination(data);
        setCurrentTermination(termination as ExtendedTerminationRecord);
      } catch (err) {
        setError("Failed to create termination record");
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  /**
   * Update termination record
   */
  const updateTermination = useCallback(
    async (
      employeeId: string,
      data: UpdateTerminationRequest,
    ): Promise<void> => {
      setIsLoading(true);
      setError(null);

      try {
        const termination = await TerminationService.updateTermination(
          employeeId,
          data,
        );
        setCurrentTermination(termination as ExtendedTerminationRecord);
      } catch (err) {
        setError("Failed to update termination record");
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  /**
   * Submit a termination for processing
   */
  const submitTermination = useCallback(
    async (employeeId: string): Promise<void> => {
      if (!validateTerminationData()) {
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const result = await TerminationService.submitTermination(employeeId);
        // TerminationService.submitTermination returns a TerminationRecord, not a TerminationResponse
        // So we need to cast it appropriately
        setCurrentTermination(result as ExtendedTerminationRecord);
      } catch (err) {
        setError("Failed to submit termination");
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    },
    [validateTerminationData],
  );

  /**
   * Update termination reason
   */
  const setTerminationReason = useCallback(
    (reason: TerminationReason): void => {
      if (!currentTermination) return;

      console.log("Updating termination reason:", reason);

      // Update local state first
      setCurrentTermination((prev) => {
        if (!prev) return null;
        return { ...prev, termination_reason: reason };
      });

      // Clear any validation error for this field
      setValidationErrors((prev) => {
        const { reason: _, ...rest } = prev;
        return rest;
      });

      // Update the backend if we have an ID
      if (currentTermination.id) {
        // Use the updateTermination service to persist the change
        TerminationService.updateTermination(currentTermination.id, {
          termination_reason: reason,
        })
          .then((updatedTermination) => {
            console.log(
              "Termination reason updated in backend:",
              updatedTermination,
            );
          })
          .catch((err) => {
            console.error("Error updating termination reason:", err);
            setError("Failed to update termination reason");
          });
      }
    },
    [currentTermination],
  );

  /**
   * Update termination date
   */
  const setTerminationDate = useCallback(
    (date: string): void => {
      if (!currentTermination) return;

      setCurrentTermination((prev) => {
        if (!prev) return null;

        // Recalculate notice period based on new termination date
        const noticeWeeks = prev.notice_calculation?.finalWeeks || 0;
        const payThroughDate = calculatePayThroughDate(date, noticeWeeks);

        const updatedNoticeCalculation: ExtendedNoticeCalculation = {
          ...(prev.noticeCalculation || {
            requiredNoticeDays: 0,
            payThroughDate: "",
            workingNoticePeriod: true,
            paymentInLieuOfNotice: false,
          }),
          payThroughDate,
        };

        return {
          ...prev,
          termination_date: date,
          noticeCalculation: updatedNoticeCalculation,
        };
      });

      // Clear any validation error for this field
      setValidationErrors((prev) => {
        const { terminationDate: _, ...rest } = prev;
        return rest;
      });
    },
    [currentTermination, calculatePayThroughDate],
  );

  /**
   * Override the notice period
   */
  const overrideNoticePeriod = useCallback(
    (weeks: number, reason: string): void => {
      if (!currentTermination) return;

      setCurrentTermination((prev) => {
        if (!prev) return null;

        // Calculate new pay-through date based on override weeks
        const payThroughDate = calculatePayThroughDate(
          prev.termination_date,
          weeks,
        );

        const noticeCalc = prev.noticeCalculation || {
          requiredNoticeDays: 0,
          payThroughDate: "",
          workingNoticePeriod: true,
          paymentInLieuOfNotice: false,
        };

        return {
          ...prev,
          noticeCalculation: {
            ...noticeCalc,
            finalWeeks: weeks,
            payThroughDate,
            overrideWeeks: {
              value: weeks,
              timestamp: new Date().toISOString(),
              reason,
            },
          },
        };
      });
    },
    [currentTermination, calculatePayThroughDate],
  );

  /**
   * Override the pay-through date directly
   */
  const overridePayThroughDate = useCallback(
    (date: string, reason: string): void => {
      if (!currentTermination) return;

      setCurrentTermination((prev) => {
        if (!prev) return null;

        const noticeCalc = prev.noticeCalculation || {
          requiredNoticeDays: 0,
          payThroughDate: "",
          workingNoticePeriod: true,
          paymentInLieuOfNotice: false,
        };

        return {
          ...prev,
          noticeCalculation: {
            ...noticeCalc,
            payThroughDate: date,
            overridePayThroughDate: {
              value: date,
              timestamp: new Date().toISOString(),
              reason,
            },
          },
        };
      });
    },
    [currentTermination],
  );

  /**
   * Override a final pay component
   */
  const overrideFinalPayComponent = useCallback(
    (name: string, value: number, reason: string): void => {
      if (!currentTermination) return;

      setCurrentTermination((prev) => {
        if (!prev) return null;

        if (!prev.final_pay_components) {
          return {
            ...prev,
            final_pay_components: [
              {
                id: `component-${Date.now()}`,
                name,
                description: name,
                amount: 0,
                type: "payment",
                pay_type: "OTHER",
                overrideValue: {
                  value,
                  timestamp: new Date().toISOString(),
                  reason,
                },
              },
            ],
          };
        }

        const updatedComponents = prev.final_pay_components?.map(
          (component) => {
            if (component.name === name) {
              return {
                ...component,
                overrideValue: {
                  value,
                  timestamp: new Date().toISOString(),
                  reason,
                },
              };
            }
            return component;
          },
        );

        return {
          ...prev,
          final_pay_components: updatedComponents,
        };
      });
    },
    [currentTermination],
  );

  /**
   * Check if the employee has an active termination process
   */
  const hasActiveTermination = useCallback(
    async (employeeId: string): Promise<boolean> => {
      try {
        return await TerminationService.hasActiveTermination(employeeId);
      } catch (err) {
        console.error("Error checking active termination:", err);
        return false;
      }
    },
    [],
  );

  // Context value
  const contextValue: TerminationContextType = {
    currentTermination,
    isLoading,
    error,
    validationErrors,
    getTermination,
    createTermination,
    updateTermination,
    submitTermination,
    setTerminationReason,
    setTerminationDate,
    overrideNoticePeriod,
    overridePayThroughDate,
    overrideFinalPayComponent,
    validateTerminationData,
    formatDateForDisplay,
    calculatePayThroughDate,
    hasActiveTermination,
    resetTermination,
    seatAction,
    seatUpdateMode,
    setSeatAction,
    setSeatUpdateMode,
  };

  return (
    <TerminationContext.Provider value={contextValue}>
      {children}
    </TerminationContext.Provider>
  );
};

/**
 * Hook to use the termination context
 */
export const useTermination = (): TerminationContextType => {
  const context = useContext(TerminationContext);

  if (context === undefined) {
    throw new Error("useTermination must be used within a TerminationProvider");
  }

  return context;
};
